import { isPowerPointApiSupported } from '@/services/office/helpers'

// The PowerPoint slide-tag primitives, extracted from services/office/insert-slides so that
// modules which only need to READ a binding do not have to depend on the module that INSERTS
// slides. insert-slides reaches @/vuex/store transitively (via utils/ppt-online.utils), and the
// store's module graph reaches back into services/office — so importing it from a leaf pulls that
// whole cycle in. Everything here depends on office/helpers alone.
//
// AHA-46329: the AhaSlides slide a PowerPoint slide is bound to lives in the PowerPoint slide's
// own TAGS, not in Office.context.document.settings. Settings cannot bind a CLONED add-in frame:
// on the web they are shared across frames and get() can return another frame's value at random
// (office-js #2765), and on desktop a cloned slide simply inherits the donor's settings snapshot.
// Either way every clone would come up rendering the same AhaSlides slide. Slide tags are
// per-slide and writable from the inserting frame, so they are the only store that can
// distinguish the clones.
// Tag keys are case-insensitive (PowerPoint stores them uppercased).
export const SLIDE_TAG_PRESENTATION_KEY = 'AHASLIDESPRESENTATIONID'
export const SLIDE_TAG_SLIDE_KEY = 'AHASLIDESLIDEID'

// TagCollection, used to bind each inserted slide.
const SLIDE_TAG_API_VERSION = '1.3'

// Can this host read/write slide tags at all? The tags are the per-slide binding that both the
// selector's insert flow and AHA-46751's auto-start ownership check rest on, so callers assert
// this directly rather than inferring it from some other requirement set.
export const canReadSlideTags = () => {
  if (!window.PowerPoint || !window.PowerPoint.run) {
    return false
  }
  return isPowerPointApiSupported(SLIDE_TAG_API_VERSION)
}

// Queues loads for the two binding tags on an already-resolved slide proxy, and returns a
// resolver to read them AFTER the next context.sync(). Shared by the index-based (desktop) and
// token-based (web) reads so the tag-shape logic lives in one place.
export const queueBindingTagRead = (slide) => {
  const presentationTag = slide.tags.getItemOrNullObject(SLIDE_TAG_PRESENTATION_KEY)
  const slideTag = slide.tags.getItemOrNullObject(SLIDE_TAG_SLIDE_KEY)
  presentationTag.load('value,isNullObject')
  slideTag.load('value,isNullObject')
  return () => ((presentationTag.isNullObject || slideTag.isNullObject)
    ? null
    : { presentationId: presentationTag.value, slideId: slideTag.value })
}

// One tag-read batch for the slide at `hostSlideIndex` (desktop path). Resolves
// { presentationId, slideId } or null; rejects if the batch errors — the caller times/retries.
// The two callers bound it differently: cold-load retries a stalled batch, the auto-start
// confirm cannot afford to.
export const readTagsAtSlideOnce = (hostSlideIndex) => window.PowerPoint.run(async (context) => {
  const resolveBinding = queueBindingTagRead(context.presentation.slides.getItemAt(hostSlideIndex - 1))
  await context.sync()
  return resolveBinding()
})

// AHA-46751: compare-and-swap the binding tags at `hostSlideIndex`. Re-reads them INSIDE the
// batch and writes only if they still hold `expected`.
//
// The guard is the whole point. The index its caller passes came from a getSelectedDataAsync
// taken before the ownership verdict was computed, and in a Slide Show the presenter can
// navigate in between — writing blind at a stale index would stamp the caller's binding onto
// whatever slide is now on screen, which is precisely the document-global-target corruption this
// ticket exists to remove. A navigation makes the re-read miss and the write is skipped.
//
// Resolves true when the write happened, false when the slide no longer holds `expected`.
// Rejects on a batch error — the caller decides how loud that is.
export const healTagsAtSlideIfUnchanged = (hostSlideIndex, expected, next) => (
  window.PowerPoint.run(async (context) => {
    const slide = context.presentation.slides.getItemAt(hostSlideIndex - 1)
    const resolveBinding = queueBindingTagRead(slide)
    await context.sync()
    const current = resolveBinding()
    // String() on both sides for the same reason every other comparison in this flow does it:
    // tags are strings, the values the caller carries are numbers.
    if (!current
      || String(current.presentationId) !== String(expected.presentationId)
      || String(current.slideId) !== String(expected.slideId)) {
      return false
    }
    slide.tags.add(SLIDE_TAG_PRESENTATION_KEY, String(next.presentationId))
    slide.tags.add(SLIDE_TAG_SLIDE_KEY, String(next.slideId))
    await context.sync()
    return true
  })
)
